if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        this._initPriceHandlers();
        this._initSubscriptionHandlers();
        this.buyButton = this.querySelector('[js-product-form-submit]');
        // The sticky bar belongs to the page's main product; a quick-add modal instance must not
        // drive it. Everything else the controller reads lives inside its own <product-info>.
        this.inModal = !!this.closest('quick-add-modal');
        this.stickyAtcBuyButton = this.inModal ? null : document.querySelector('[js-sticky-atc-add-to-cart]');
        this.price = this.dataset.price;
        this.subscriptionPrice = this.dataset.subscriptionPrice;
        // The selected variant's regular price as a number. Seeded from the server render and
        // refreshed on every variant change (the data attributes only describe the first variant).
        this.variantAmount = this._amount(this.price);

        // Keep the main price block (which may hold the sitewide promo markup) so we can
        // restore it for one-time and swap to the app's subscription price for subscriptions.
        this.mainPriceEl = this.querySelector('.product-info__price [js-price]');
        this.mainPricePromoHtml = this.mainPriceEl ? this.mainPriceEl.innerHTML : null;
        // [js-price] is the .price root itself: its state classes (price--promo, price--on-sale)
        // are not part of innerHTML, so they are kept alongside for the captured markup.
        this.mainPriceClass = this.mainPriceEl ? this.mainPriceEl.className : '';
        // The one-time price (already includes the sitewide promo when active), captured from the
        // server render. Used for the ATC button and the subscription widget's one-time row.
        this.onetimePrice = this._mainFinalPrice();
        this.promoSubscriptions = this.dataset.promoSubscriptions !== 'false';
        // One stable bound handler so re-binding after widget re-renders never stacks duplicates.
        this._boundRechargeChange = this._handleRechargeInputChange.bind(this);
        // The promo note under the sticky bar's button: hidden while a subscription is selected.
        this.stickyPromoNote = this.inModal ? null : document.querySelector('.product__sticky-atc-buy .price__promo-badge-under');

        // A variant change re-renders the main price block server-side: re-capture the promo
        // markup and prices for the new variant, then re-apply the selected purchase option.
        const mainPriceContainer = this.querySelector('.product-info__price');
        if (mainPriceContainer) {
          const resync = new MutationObserver(() => this._resyncMainPrice());
          resync.observe(mainPriceContainer, { childList: true, subtree: true });
        }

        customElements.whenDefined('recharge-subscription-widget').then(() => {
          const widget = this.querySelector('recharge-subscription-widget');
          if (!widget) return;

          // Poll for the shadow root, but give up after ~10s so the interval never runs forever.
          let shadowAttempts = 0;
          const waitForShadow = setInterval(() => {
            if (!widget.shadowRoot) {
              shadowAttempts += 1;
              if (shadowAttempts > 200) clearInterval(waitForShadow);
              return;
            }

            clearInterval(waitForShadow);
            this.rechargeWidget = widget;

            // The widget renders and re-renders asynchronously (including on variant change,
            // which swaps the radios), so re-sync on every shadow DOM change.
            this._syncRechargeWidget();
            const observer = new MutationObserver(() => this._syncRechargeWidget());
            observer.observe(widget.shadowRoot, { childList: true, subtree: true, characterData: true });
          }, 50);
        });
      }

      // Re-capture the price block after the theme replaced it (variant change).
      _resyncMainPrice() {
        const el = this.querySelector('.product-info__price [js-price]');
        // Same node means the change was one of our own writes, not a server re-render.
        if (!el || el === this.mainPriceEl) return;
        this.mainPriceEl = el;
        this.mainPricePromoHtml = el.innerHTML;
        this.mainPriceClass = el.className;
        this.onetimePrice = this._mainFinalPrice();
        this._syncRechargeWidget();
      }

      // Bind the widget radios (idempotent) and re-apply the checked option's pricing.
      _syncRechargeWidget() {
        const widget = this.rechargeWidget || this.querySelector('recharge-subscription-widget');
        if (!widget || !widget.shadowRoot) return;
        const radios = Array.from(widget.shadowRoot.querySelectorAll('.rc-purchase-option__input'));
        if (!radios.length) {
          // No inputs found. Only treat this as "variant without purchase options" when the
          // widget is genuinely empty; if option rows exist but the input class changed
          // (a Recharge update), bail instead of forcing the one-time view over a selected subscription.
          if (widget.shadowRoot.querySelector('.rc-purchase-option')) return;
          // Variant without purchase options: the server-rendered prices are the truth. Restore the
          // one-time presentation, and take the previous variant's subscription price off the buttons.
          this._setMainPrice(true);
          if (this.stickyPromoNote) this.stickyPromoNote.hidden = false;
          this._resetButtonsToOnetime();
          return;
        }
        radios.forEach((input) => input.addEventListener('change', this._boundRechargeChange));
        // The widget re-renders a moment after the theme swaps the variant, so its rows can still
        // belong to the previous variant here. Its regular price must match the selected variant
        // before its option pricing is applied; the shadow DOM observer re-runs this once it renders.
        if (this._widgetIsStale()) return;
        const checked = radios.find((input) => input.checked);
        if (checked) this._applyPurchaseOption(checked.value);
        this._updateWidgetOnetimePrice();
      }

      // Buttons we switched to a subscription price get the one-time render back (final price plus
      // the struck price from the main block); buttons still holding the server render
      // (no data-spf-html) are left as rendered.
      _resetButtonsToOnetime() {
        this._writeButtons((target) => this._capturedBlockButtonHtml(target.withCompareAt) || this._twoPriceHtml(this.onetimePrice, null), true);
      }

      // The widget still shows the previous variant: its own regular price differs from the
      // selected variant's. Read from the one-time row (always the plain variant price); the
      // subscription strike-through is only a fallback, since it can be a prepaid total.
      _widgetIsStale() {
        const original = this._widgetOnetimeOriginal() || this._widgetOriginalPrice();
        if (!original || this.variantAmount == null) return false;
        return !this._sameAmount(original, this.variantAmount);
      }

      // Money string ("$1,234.00", "$34") to a number; null when nothing numeric is inside.
      _amount(value) {
        if (typeof value === 'number') return value;
        const match = String(value || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
        return match ? parseFloat(match[0]) : null;
      }

      // Same money amount regardless of formatting: the legacy price sync strips ".00", so the
      // same price can read "$34" in one place and "$34.00" in another.
      _sameAmount(a, b) {
        const x = this._amount(a);
        const y = this._amount(b);
        if (x == null || y == null) return String(a) === String(b);
        return Math.abs(x - y) < 0.005;
      }

      _handleRechargeInputChange(e) {
        this._applyPurchaseOption(e.currentTarget.value);
        this._updateWidgetOnetimePrice();
      }

      // Route the main price block, ATC and sticky ATC to the chosen purchase option.
      _applyPurchaseOption(value) {
        const isOnetime = value === 'onetime';
        if (!isOnetime) {
          // Prefer the live subscription price from the widget: the data attribute only
          // holds the first variant's price and goes stale after a variant change.
          const live = this._widgetSubscriptionPrice();
          if (live) this.subscriptionPrice = live;
        }
        this._setMainPrice(isOnetime);
        // The promo note only describes the one-time promo, so hide it for subscriptions.
        if (this.stickyPromoNote) this.stickyPromoNote.hidden = !isOnetime;
        if (isOnetime) {
          // One-time: the server-rendered main block is the truth (promo or compare-at), the
          // widget only supplies a fallback original when the block cannot be read.
          this._writeButtons((target) => this._capturedBlockButtonHtml(target.withCompareAt) || this._optionPriceHtml(this.onetimePrice));
          return;
        }
        if (!this.subscriptionPrice) return;
        // Subscription: the app's price plus the struck original, matching the widget row.
        const html = this._optionPriceHtml(this.subscriptionPrice);
        this._writeButtons(() => html);
      }

      // The two buy buttons the controller keeps in sync. The sticky bar's server render never
      // shows the compare-at (product__sticky-atc.liquid renders without show_compare_at_price),
      // the main ATC does (buy-buttons.liquid), so each target says what its render may carry.
      _buttonTargets() {
        return [
          { el: this.buyButton && this.buyButton.querySelector('[js-price]'), withCompareAt: true },
          { el: this.stickyAtcBuyButton && this.stickyAtcBuyButton.querySelector('[js-price]'), withCompareAt: false },
        ].filter((target) => target.el);
      }

      // Write a render per button. A render that shows the same amounts as the current one is
      // left alone: the theme's price sync strips ".00" from the main block, so the same price can
      // come back as "$34" after "$34.00" and would otherwise rewrite (and flicker) for nothing.
      _writeButtons(renderFor, onlyOurs) {
        this._buttonTargets().forEach((target) => {
          const el = target.el;
          if (onlyOurs && !el.dataset.spfHtml) return;
          const html = renderFor(target);
          if (!html || el.dataset.spfHtml === html) return;
          if (el.dataset.spfHtml && this._sameRender(el.dataset.spfHtml, html)) return;
          el.innerHTML = html;
          el.dataset.spfHtml = html;
        });
      }

      // Two renders show the same amounts in the same order.
      _sameRender(a, b) {
        const x = this._amounts(a);
        const y = this._amounts(b);
        return x.length === y.length && x.every((value, i) => Math.abs(value - y[i]) < 0.005);
      }

      _amounts(value) {
        return (String(value || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/g) || []).map(parseFloat);
      }

      // Option price plus the struck original from the widget (the variant's regular price).
      _optionPriceHtml(price) {
        return this._twoPriceHtml(price, this._widgetOriginalPrice());
      }

      // Price plus a struck original, ordered by the theme setting; just the price when there is no
      // original or it is the same amount. Spacing via margin: flex layouts (the buttons) drop
      // text-node whitespace between items.
      _twoPriceHtml(price, original) {
        // A lone price keeps its cents ("$16.00"), like the server-rendered button on page load.
        if (!original || this._sameAmount(original, price)) return this._moneyWithCents(price);
        // A pair drops trailing zeros ("$30.60 $34"), the theme's compact format (the price sync
        // strips ".00" from every .price-item the same way).
        price = this._moneyCompact(price);
        original = this._moneyCompact(original);
        const strikeFirst = this.dataset.promoPriceOrder === 'strike_first';
        const struck = '<s style="opacity: 0.6; margin-' + (strikeFirst ? 'right' : 'left') + ': 0.35em;">' + original + '</s>';
        return strikeFirst ? struck + price : price + struck;
      }

      _moneyCompact(value) {
        return String(value || '').replace(/\.00(?!\d)/, '');
      }

      _moneyWithCents(value) {
        const text = String(value || '');
        if (/\.\d\d(?!\d)/.test(text)) return text;
        if (/\.\d(?!\d)/.test(text)) return text.replace(/(\.\d)(?!\d)/, '$10');
        return text.replace(/(\d)(?![\d.,])/, '$1.00');
      }

      // The one-time button render derived from the main price block's server markup: final price
      // plus the struck price the button would show. Under the sitewide promo that is the crossed-out
      // current price (never the compare-at, which the buttons hide), on a plain compare-at sale the
      // compare-at itself, otherwise nothing.
      _blockButtonHtml(root, withCompareAt) {
        const container = root || this.mainPriceEl;
        if (!container) return null;
        const finalEl = container.querySelector('.price-item--sale.price-item--last');
        if (!finalEl) return null;
        const price = finalEl.textContent.trim();
        const priceRoot = container.matches('.price') ? container : container.querySelector('.price') || container;
        const struckEls = Array.from(container.querySelectorAll('.price__sale s.price-item--regular'));
        let struck = null;
        if (priceRoot.classList.contains('price--promo')) {
          struck = struckEls.find((el) => this._sameAmount(el.textContent, this.variantAmount)) || null;
          if (!struck && struckEls.length === 1) struck = struckEls[0];
        } else if (withCompareAt && priceRoot.classList.contains('price--on-sale')) {
          struck = struckEls[0] || null;
        }
        return this._twoPriceHtml(price, struck ? struck.textContent.trim() : null);
      }

      // Same as _blockButtonHtml, but from the captured server markup of the main block (the live
      // node may hold our subscription render at the time).
      _capturedBlockButtonHtml(withCompareAt) {
        if (this.mainPricePromoHtml == null) return null;
        const scratch = document.createElement('div');
        scratch.className = this.mainPriceClass || '';
        scratch.innerHTML = this.mainPricePromoHtml;
        return this._blockButtonHtml(scratch, withCompareAt);
      }

      // The variant's regular price, read live from the widget (survives variant changes):
      // the struck price in the subscription row, or the original of the one-time row.
      _widgetOriginalPrice() {
        const widget = this.rechargeWidget || this.querySelector('recharge-subscription-widget');
        if (!widget || !widget.shadowRoot) return null;
        const struck = widget.shadowRoot.querySelector('.rc-purchase-option .rc-price.strike-through');
        const match = struck && struck.textContent.match(/\$[\d.,]+/);
        if (match) return match[0];
        return this._widgetOnetimeOriginal();
      }

      // The one-time row's price element in the widget, if the row exists.
      _widgetOnetimePriceEl() {
        const widget = this.rechargeWidget || this.querySelector('recharge-subscription-widget');
        if (!widget || !widget.shadowRoot) return null;
        const input = widget.shadowRoot.querySelector('.rc-purchase-option__input[value="onetime"]');
        const option = input && input.closest('.rc-purchase-option');
        return (option && option.querySelector('.rc-price')) || null;
      }

      _widgetOnetimeOriginal() {
        const priceEl = this._widgetOnetimePriceEl();
        return priceEl ? this._onetimeRowOriginal(priceEl) : null;
      }

      // The app's own price for the one-time row. Recharge may update the text in place and keep
      // the node, so the cached data-spf-original is trusted only while our struck markup is still
      // inside; plain text means a fresh render and refreshes the cache.
      _onetimeRowOriginal(priceEl) {
        if (!priceEl.querySelector('s')) {
          const match = priceEl.textContent.match(/\$[\d.,]+/);
          if (match) {
            priceEl.dataset.spfOriginal = match[0];
            return match[0];
          }
        }
        return priceEl.dataset.spfOriginal || null;
      }

      // One-time: restore the main price block (with promo). Subscription: the app's price
      // plus the struck original, matching the widget's subscription row.
      _setMainPrice(isOnetime) {
        if (!this.mainPriceEl) return;
        if (isOnetime) {
          if (this.mainPricePromoHtml != null && this.mainPriceEl.innerHTML !== this.mainPricePromoHtml) {
            this.mainPriceEl.innerHTML = this.mainPricePromoHtml;
            delete this.mainPriceEl.dataset.spfHtml;
          }
        } else if (this.subscriptionPrice) {
          const html = this._optionPriceHtml(this.subscriptionPrice);
          if (this.mainPriceEl.dataset.spfHtml !== html) {
            this.mainPriceEl.innerHTML = html;
            this.mainPriceEl.dataset.spfHtml = html;
          }
        }
      }

      // The final price shown in the main block (promo price when the sitewide promo is on, else the current price).
      _mainFinalPrice() {
        const el = this.mainPriceEl && this.mainPriceEl.querySelector('.price-item--sale.price-item--last');
        return el ? el.textContent.trim() : this.price;
      }

      // The app's current (non struck-through) subscription price, read live from the widget.
      _widgetSubscriptionPrice() {
        const widget = this.rechargeWidget || this.querySelector('recharge-subscription-widget');
        if (!widget || !widget.shadowRoot) return null;
        const input = Array.from(widget.shadowRoot.querySelectorAll('.rc-purchase-option__input')).find(
          (radio) => radio.value !== 'onetime'
        );
        const option = input && input.closest('.rc-purchase-option');
        if (!option) return null;
        const priceEl = Array.from(option.querySelectorAll('.rc-price')).find(
          (el) => !el.classList.contains('strike-through')
        );
        const match = priceEl && priceEl.textContent.match(/\$[\d.,]+/g);
        return match ? match[match.length - 1] : null;
      }

      // Mirror the promo into the Recharge widget's one-time row (in its shadow DOM): the promo
      // price plus the struck original, ordered by the theme's "Final price position" setting.
      // The subscription row keeps the app's own price. Skipped when promo is off for subscription products.
      _updateWidgetOnetimePrice() {
        if (!this.promoSubscriptions || !this.onetimePrice) return;
        const widget = this.rechargeWidget || this.querySelector('recharge-subscription-widget');
        if (!widget || !widget.shadowRoot) return;
        widget.shadowRoot.querySelectorAll('.rc-purchase-option__input').forEach((input) => {
          if (input.value !== 'onetime') return;
          const option = input.closest('.rc-purchase-option');
          const priceEl = option && option.querySelector('.rc-price');
          if (!priceEl) return;
          const original = this._onetimeRowOriginal(priceEl);
          if (!original) return;
          if (this._sameAmount(original, this.onetimePrice)) {
            if (priceEl.dataset.spfApplied) {
              priceEl.textContent = original;
              delete priceEl.dataset.spfApplied;
            }
            return;
          }
          const key = original + '|' + this.onetimePrice;
          if (priceEl.dataset.spfApplied === key) return;
          const struck = '<s style="opacity: 0.6;">' + original + '</s>';
          priceEl.innerHTML =
            this.dataset.promoPriceOrder === 'strike_first'
              ? struck + ' ' + this.onetimePrice
              : this.onetimePrice + ' ' + struck;
          priceEl.dataset.spfOriginal = original;
          priceEl.dataset.spfApplied = key;
        });
      }

      _initSubscriptionHandlers() {
        const subscriptionFields = document.querySelector('.shopify_subscriptions_app_block_label_children');
        if (subscriptionFields) {
          const inputs = subscriptionFields.querySelectorAll('input');
          inputs[0].click();
        }
      }

      _initPriceHandlers() {
        const config = { attributes: true, childList: true, subtree: true };
        const callback = (mutationList, observer) => {
          const lastMutation = mutationList[mutationList.length - 1];
          const lastMutationTarget = lastMutation.target;
          lastMutationTarget.dispatchEvent(new CustomEvent('price:changed', { bubbles: true }, {'detail': lastMutationTarget}));
        };
        const observer = new MutationObserver(callback);

        // The main price block is controlled explicitly (it may hold rich promo markup),
        // so keep it out of the generic price sync that flattens every [js-price] to one value.
        this.querySelectorAll('[js-price]').forEach(price => {
          if (price.closest('.product-info__price')) return;
          observer.observe(price, config);
        });

        document.addEventListener('price:changed', (event) => {
          const allPrices = document.querySelectorAll('.price-item');
          allPrices.forEach(price => {
            if (price.innerHTML.includes('USD')) {
              price.innerHTML = price.innerHTML.replace('USD', '');
            }
            if (price.innerHTML.includes('.00')) {
              price.innerHTML = price.innerHTML.replace('.00', '');
            }
          });
          const price = event.target;
          const priceValue = price.innerHTML;

          observer.disconnect();
          this.querySelectorAll('[js-price]').forEach(price => {
            if (price.closest('.product-info__price')) return;
            price.innerHTML = priceValue;
            observer.observe(price, config);
          });

        });

        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = (event && event.data) || {};
          // Other product-info instances publish too (quick-add modals from the recommendations,
          // a featured-product section): only this section's own variant drives this controller.
          if (data.sectionId && data.sectionId !== this.sectionId) return;
          const variant = data.variant;
          if (variant && typeof variant.price === 'number') this.variantAmount = variant.price / 100;
          const priceContainer = this.querySelector('.product-info__price [js-price]');
          if (!priceContainer) return;
          // The buttons mirror the freshly rendered main block: final price plus the struck price
          // (promo current price, or the compare-at where the button's own render shows it).
          // Nothing to mirror while the block shows the plain subscription price; the widget sync
          // handles that case.
          this._writeButtons((target) => this._blockButtonHtml(priceContainer, target.withCompareAt));
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
